import React, { useCallback, useEffect } from "react";
import "./App.css";
import { Navigate, Route, Routes } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.css";

// Styles
// CoreUI Icons Set
// import "@coreui/icons/css/all.css";
// Import Flag Icons Set
import "flag-icon-css/css/flag-icon.min.css";
// Import Font Awesome Icons Set
import "@fortawesome/fontawesome-free/css/solid.css";
import "@fortawesome/fontawesome-free/css/brands.css";
import "@fortawesome/fontawesome-free/css/fontawesome.css";

// Import Simple Line Icons Set
import "simple-line-icons/css/simple-line-icons.css";
// Import Main styles for this application
import "./layout/scss/style.scss";
import ShopifyRegistrationPage from "./integrations/shop/Shopify/RegistrationScreen/ShopifyRegistrationScreen";
import StripeConnectLoginPage from "./integrations/Stripe/Connect/StripeConnectLoginScreen";
import LexOfficeAutoLogin from "./integrations/LexOffice/screens/LexOfficeAutoLogin";
import LexOfficeRegistration from "./integrations/LexOffice/screens/LexOfficeRegistration";
import LogoutScreen from "./user/screens/LogoutScreen";
import ResetPasswordScreen from "./user/screens/ResetPasswordScreen";
import ActivationScreen from "./user/screens/ActivationScreen";

import StripeMarketplaceRegistrationPage from "./integrations/Stripe/MarketplaceRegistrationScreen/screens/StripeMarketplaceRegistrationScreen";
import PrivateRoute from "./_components/PrivateRoute";
import { CustomRouter } from "./layout/components/CustomRouter";
import { history } from "./_services/history";
import DefaultLayout from "./layout/components/DefaultLayout";
import ProductFruitsConnector from "./commons/components/ProductFruitsConnector";
import TagManager from "react-gtm-module";
import ReactPixel from "react-facebook-pixel";
import { useSelector } from "react-redux";
import useCelloAttribution from "./_hooks/useCelloAttribution";
import CelloLandingScreen from "./user/screens/CelloLandingScreen";
import useAnalytics from "./_hooks/useAnalytics";
import useFinMessenger from "./_hooks/useFinMessenger";
import LoginScreenV2 from "./user/screens/LoginScreenV2";
import ForgotPasswordScreen from "./user/screens/ForgotPasswordScreen";
import InternalLoginScreen from "./user/screens/InternalLoginScreen";
import TikTokRegistrationScreen from "./authentication/screens/TikTokRegistrationScreen";
import ResetPasswordCustomisedScreen from "./user/screens/ResetPasswordCustomisedScreen";
import RegistrationScreen from "./authentication/registration/RegistrationScreen";

const App = () => {
    useEffect(() => {
        const tagManagerArgs = {
            gtmId: process.env.REACT_APP_GTM_ID,
        };
        TagManager.initialize(tagManagerArgs);
        ReactPixel.init(process.env.REACT_APP_FB_GRAPH_PIXEL_ID, null, { debug: true });
    }, []);

    const user = useSelector((state) => state.authentication.user);
    const { initializeCelloAttribution } = useCelloAttribution();
    const { isProduction } = useAnalytics();

    useFinMessenger(user);

    let observer = null;

    if (isProduction) {
        observer = new MutationObserver((mutations, observerInstance) => {
            try {
                const host = document.querySelector("#cello-widget-app");
                if (!host || !host.shadowRoot) return;

                const shadow = host.shadowRoot;
                const celloWidgetButton = shadow.querySelector(".cello-rzi8hc"); // same class name in sandbox and prod

                // Only tag if found, don't require all at once
                if (celloWidgetButton && !celloWidgetButton.hasAttribute("data-cy")) {
                    celloWidgetButton.setAttribute("data-cy", "cello-widget-button-launcher");
                }

                // Disconnect once done
                if (celloWidgetButton?.hasAttribute("data-cy")) {
                    observerInstance.disconnect();
                }
            } catch (error) {
                // Never allow third-party observers to break the app
                console.error("Cello MutationObserver failed safely:", error);
            }
        });
    }

    const initializeCello = useCallback(async () => {
        if (!isProduction) {
            return;
        }
        window.cello = window.cello || { cmd: [] };
        if (user?.celloToken) {
            window.cello.cmd.push(async function (cello) {
                try {
                    await cello.boot({
                        productId: process.env.REACT_APP_CELLO_PRODUCT_ID,
                        token: user.celloToken,
                        language: "de",
                        productUserDetails: {
                            firstName: user?.firstName,
                            lastName: user?.lastName,
                            fullName: user?.fullName,
                            email: user?.mail,
                        },
                        onOpen: () => {
                            setTimeout(() => {
                                const host = document.querySelector("#cello-widget-app");
                                const shadow = host.shadowRoot;
                                const celloWidgetPopup = shadow.querySelector(".widget-wrapper-enter-done");
                                const celloWidgetReferralLink = shadow.querySelector(".cello-1vkix9w"); // same class name in sandbox and prod

                                if (celloWidgetPopup && !celloWidgetPopup.hasAttribute("data-cy")) {
                                    celloWidgetPopup.setAttribute("data-cy", "cello-widget-popup");
                                }

                                if (celloWidgetReferralLink && !celloWidgetReferralLink.hasAttribute("data-cy")) {
                                    celloWidgetReferralLink.setAttribute("data-cy", "cello-widget-referral-link");
                                }
                            }, 1000);
                        },
                    });
                    observer?.observe(document.body, { childList: true, subtree: true });
                } catch (error) {
                    console.error("Failed to boot cello:", error);
                }
            });
        }
    }, [user?.celloToken, user?.firstName, user?.lastName, user?.fullName, user?.mail, isProduction, observer]);

    useEffect(() => {
        initializeCelloAttribution();
    }, [initializeCelloAttribution]);

    useEffect(() => {
        initializeCello();
    }, [initializeCello]);

    useEffect(() => {
        const handler = (event) => {
            const reason = event?.reason;
            const message = reason?.message || "";
            const stack = reason?.stack || "";

            // Intercom Fin AI evaluation timeout
            if (message.includes("Evaluation timeout") || stack.includes("aistudio")) {
                console.warn("Fin AI evaluation timeout safely handled");
                event.preventDefault();
            }
        };

        window.addEventListener("unhandledrejection", handler);

        return () => {
            window.removeEventListener("unhandledrejection", handler);
        };
    }, []);

    return (
        <>
            <ProductFruitsConnector />

            <CustomRouter history={history}>
                <Routes>
                    <Route exact path="/cello" element={<CelloLandingScreen />} />
                    <Route exact path="/registration" element={<RegistrationScreen />} />
                    <Route
                        exact
                        path="/register"
                        element={<Navigate to={`/registration${window.location.search}`} replace />}
                    />
                    <Route exact path="/shopify/register" element={<ShopifyRegistrationPage />} />
                    <Route exact path="/tiktok/register" element={<TikTokRegistrationScreen />} />
                    <Route exact path="/stripemarketplace" element={<StripeMarketplaceRegistrationPage />} />
                    <Route exact path="/stripeconnect" element={<StripeConnectLoginPage />} />
                    <Route exact path="/lexoffice/register" element={<LexOfficeRegistration />} />
                    <Route exact path="/lexoffice/autologin" element={<LexOfficeAutoLogin />} />
                    <Route exact path="/login" element={<InternalLoginScreen />} />
                    <Route exact path="/login-old" element={<LoginScreenV2 />} />

                    <Route exact path="/user/login" element={<LoginScreenV2 />} />
                    <Route exact path="/user/forgot-password" element={<ForgotPasswordScreen />} />
                    <Route exact path="/activate/:id" element={<ActivationScreen />} />
                    <Route exact path="/activate" element={<ActivationScreen />} />
                    <Route exact path="/resetpassword/:id" element={<ResetPasswordScreen />} />
                    <Route exact path="/reset-password" element={<ResetPasswordCustomisedScreen />} />
                    <Route exact path="/resetpassword" element={<ResetPasswordScreen />} />
                    <Route exact path="/dologout" element={<LogoutScreen />} />
                    <Route path="*" element={<PrivateRoute />}>
                        <Route path="*" element={<DefaultLayout />} />
                    </Route>
                </Routes>
            </CustomRouter>
        </>
    );
};

export default App;
